import type {
	FocusAskComment,
	FocusAskLink,
} from '@atlassian/conversation-assistant-message-actions-api/focus-ask';

import { identityAriFromResourceId } from './atlassian-refs';
import { parseStringToIsoDay } from './format-focus-ask-target-date';
import type { FocusAskFormData } from './types';

/**
 * Builds the canonical `comments` array from the editable comment ADF string.
 * Returns an empty array when the user left it blank so the BE sees an empty
 * collection rather than a stale `[{ data: '' }]` entry.
 */
function buildComments(commentData: string): FocusAskComment[] {
	const trimmed = commentData.trim();
	return trimmed ? [{ data: trimmed }] : [];
}

/**
 * Builds the canonical `links` array from the editable links. Empty URL rows
 * are omitted; only writes `linkText` when present so the shape stays minimal.
 */
function buildLinks(links: FocusAskFormData['links']): FocusAskLink[] {
	return links
		.map((link): FocusAskLink | null => {
			const trimmedUrl = link.url.trim();
			const trimmedText = link.linkText?.trim() ?? '';
			if (!trimmedUrl) {
				return null;
			}
			return trimmedText ? { url: trimmedUrl, linkText: trimmedText } : { url: trimmedUrl };
		})
		.filter((link): link is FocusAskLink => Boolean(link));
}

/**
 * Builds the payload after the user edits the Focus Ask card/modal.
 *
 * The BE wire shape uses nested arrays for comments and links
 * (`comments: [{ data }]`, `links: [{ url, linkText }]`). Older payloads still
 * occasionally carry the flat `commentData` / `linkUrl` / `linkText` fields;
 * we strip those so the merged result only carries the canonical nested shape.
 */
export function mergeFocusAskFormIntoPayload(
	originalData: unknown,
	formData: FocusAskFormData,
): Record<string, unknown> {
	const base: Record<string, unknown> =
		typeof originalData === 'object' && originalData !== null && !Array.isArray(originalData)
			? { ...(originalData as Record<string, unknown>) }
			: {};
	for (const key of [
		'submitterAccountId',
		'ownerAccountId',
		'submitter',
		'owner',
		'submittingTeam',
		'receivingTeam',
		'submitterId',
		'ownerId',
		'submittingTeamId',
		'receivingTeamId',
		// Legacy flat shape — superseded by `comments` / `links` arrays below.
		'commentData',
		'linkUrl',
		'linkText',
	] as const) {
		delete base[key];
	}
	const targetDateIso = parseStringToIsoDay(formData.targetDate.targetDate);
	delete base.targetDateType;
	delete base.impactedItem;

	const result: Record<string, unknown> = {
		...base,
		name: formData.name,
		description: formData.description,
		justification: formData.justification,
		...(targetDateIso
			? { targetDate: { targetDate: targetDateIso, targetDateType: 'DAY' as const } }
			: { targetDate: null }),
		comments: buildComments(formData.commentData),
		links: buildLinks(formData.links),
		priority: formData.priority,
		impactedWorkId: formData.impactedWork?.id ?? null,
		...(formData.submitter
			? { submitterId: identityAriFromResourceId(formData.submitter.id, 'user') }
			: {}),
		...(formData.owner ? { ownerId: identityAriFromResourceId(formData.owner.id, 'user') } : {}),
		...(formData.submittingTeam
			? { submittingTeamId: identityAriFromResourceId(formData.submittingTeam.id, 'team') }
			: {}),
		...(formData.receivingTeam
			? { receivingTeamId: identityAriFromResourceId(formData.receivingTeam.id, 'team') }
			: {}),
	};
	return result;
}
